export const ORDER_PRODUCT_ACTIONS = {
  /** 사용하기 */
  USING: 'ORDER_PRODUCT_ACTION_USING',
  /** 배송조회 */
  DELIVERY_TRACKING: 'ORDER_PRODUCT_ACTION_DELIVERY_TRACKING',
  /** 재구매 */
  REPURCHASE: 'ORDER_PRODUCT_ACTION_REPURCHASE',
  /** 구매확정 */
  PURCHASE_CONFIRM: 'ORDER_PRODUCT_ACTION_PURCHASE_CONFIRM',
  /** 후기작성 */
  REVIEW_WRITE: 'ORDER_PRODUCT_ACTION_REVIEW',
  /** 반품 */
  RETURN: 'ORDER_PRODUCT_ACTION_RETURN',
  /** 주문취소 */
  CANCEL: 'ORDER_PRODUCT_ACTION_CANCEL',
  /** 모바일교환권 주문 취소 */
  MOBILE_COUPON_CANCEL: 'ORDER_PRODUCT_ACTION_MOBILE_COUPON_CANCEL',
  /** 요청 취소 (반품 요청 철회) */
  CANCEL_CLAIM: 'ORDER_PRODUCT_ACTION_CANCEL_CLAIM',
} as const;

export const ORDER_PRODUCT_ACTION_LABELS = {
  [ORDER_PRODUCT_ACTIONS.USING]: '사용하기',
  [ORDER_PRODUCT_ACTIONS.DELIVERY_TRACKING]: '배송조회',
  [ORDER_PRODUCT_ACTIONS.REPURCHASE]: '재구매',
  [ORDER_PRODUCT_ACTIONS.PURCHASE_CONFIRM]: '구매확정',
  [ORDER_PRODUCT_ACTIONS.REVIEW_WRITE]: '후기작성',
  [ORDER_PRODUCT_ACTIONS.RETURN]: '반품',
  [ORDER_PRODUCT_ACTIONS.CANCEL]: '주문취소',
  [ORDER_PRODUCT_ACTIONS.MOBILE_COUPON_CANCEL]: '주문취소',
  [ORDER_PRODUCT_ACTIONS.CANCEL_CLAIM]: '요청 취소',
} as const;

// value: 주문 상태(orderStatus) — API 필터 파라미터로 사용
// label: 상품 상태(productStatus) 기준 한글명 — PRODUCT_ORDER_STATUS_LABEL 기반 표기
export const ORDER_STATUS_OPTIONS = [
  { label: '상태 전체', value: '' as const },
  { label: '주문 완료', value: 'PAYMENT_COMPLETED' as const },
  { label: '상품 준비중', value: 'DELIVERY_PREPARING' as const },
  { label: '배송중', value: 'IN_DELIVERY' as const },
  { label: '배송 완료', value: 'DELIVERY_COMPLETED' as const },
  { label: '구매 확정', value: 'PURCHASE_CONFIRMED' as const },
  { label: '취소 완료', value: 'ORDER_CANCELLED' as const },
  { label: '반품중', value: 'RETURN_PROCESSING' as const },
  { label: '환불 완료', value: 'REFUND_COMPLETED' as const },
];

type DatePeriodOption = {
  label: string;
  value: 'today' | '1week' | '1month' | '3months' | '6months' | '1year';
  amount: number;
  unit: 'day' | 'month' | 'year';
};

type AllPeriodOption = {
  label: string;
  value: 'all';
};

export type PeriodOption = DatePeriodOption | AllPeriodOption;

export const PERIOD_OPTIONS: PeriodOption[] = [
  { label: '오늘', value: 'today' as const, amount: 0, unit: 'day' as const },
  { label: '일주일', value: '1week' as const, amount: 7, unit: 'day' as const },
  {
    label: '1개월',
    value: '1month' as const,
    amount: 1,
    unit: 'month' as const,
  },
  {
    label: '3개월',
    value: '3months' as const,
    amount: 3,
    unit: 'month' as const,
  },
  {
    label: '6개월',
    value: '6months' as const,
    amount: 6,
    unit: 'month' as const,
  },
  {
    label: '1년',
    value: '1year' as const,
    amount: 1,
    unit: 'year' as const,
  },
  { label: '기간 전체', value: 'all' as const },
];

export const getActionsForStatus = (
  status?: string,
  isOnlineProduct?: boolean,
  hasReview?: boolean,
  orderType?: string,
) => {
  // 포인트마켓 상품 → 사용전 상태에서 사용하기만 표시
  if (orderType === 'POINT_MARKET') {
    const usableStatuses = [
      'PRODUCT_ORDERED',
      'COUPON_ISSUING',
      'COUPON_ISSUED',
      'COUPON_FAILED',
    ];

    return usableStatuses.includes(status ?? '')
      ? [ORDER_PRODUCT_ACTIONS.USING]
      : [];
  }

  // 기부선물 (donation) 상품 — productStatus가 null이므로 orderStatus 기반
  if (orderType === 'GIFT') {
    switch (status) {
      case 'IN_DELIVERY': // 배송중
        return [ORDER_PRODUCT_ACTIONS.DELIVERY_TRACKING];
      case 'DELIVERY_COMPLETED': // 배송완료
        return hasReview
          ? [
              ORDER_PRODUCT_ACTIONS.DELIVERY_TRACKING,
              ORDER_PRODUCT_ACTIONS.PURCHASE_CONFIRM,
            ]
          : [
              ORDER_PRODUCT_ACTIONS.DELIVERY_TRACKING,
              ORDER_PRODUCT_ACTIONS.PURCHASE_CONFIRM,
              ORDER_PRODUCT_ACTIONS.REVIEW_WRITE,
            ];
      case 'PURCHASE_CONFIRMED': // 구매확정
        return hasReview ? [] : [ORDER_PRODUCT_ACTIONS.REVIEW_WRITE];
      default:
        return [];
    }
  }

  // 온라인 상품 (모바일교환권/디지털 쿠폰)
  if (isOnlineProduct) {
    switch (status) {
      case 'PRODUCT_ORDERED': // 주문완료 → 쿠폰 사용 또는 취소 가능
        return [
          ORDER_PRODUCT_ACTIONS.USING,
          ORDER_PRODUCT_ACTIONS.MOBILE_COUPON_CANCEL,
        ];
      default:
        return [];
    }
  }

  // 일반 상품 (배송 상품)
  switch (status) {
    case 'PRODUCT_ORDERED': // 주문완료 (결제 직후)
    case 'PRODUCT_PREPARING': // 상품준비중 (판매자가 배송 준비)
      return [ORDER_PRODUCT_ACTIONS.CANCEL];
    case 'PRODUCT_SHIPPING': // 배송중
      return [ORDER_PRODUCT_ACTIONS.DELIVERY_TRACKING];
    case 'PRODUCT_DELIVERED': // 배송완료
      return [
        ORDER_PRODUCT_ACTIONS.PURCHASE_CONFIRM,
        ORDER_PRODUCT_ACTIONS.RETURN,
      ];
    case 'PRODUCT_CONFIRMED': // 구매확정 (후기 수정/삭제는 내가 남긴 후기 페이지에서 처리)
      return hasReview
        ? [ORDER_PRODUCT_ACTIONS.REPURCHASE]
        : [
            ORDER_PRODUCT_ACTIONS.REPURCHASE,
            ORDER_PRODUCT_ACTIONS.REVIEW_WRITE,
          ];
    case 'PRODUCT_CANCELLED': // 주문취소 완료
    case 'PRODUCT_REFUND_COMPLETED': // 환불완료
      return [ORDER_PRODUCT_ACTIONS.REPURCHASE];
    case 'PRODUCT_RETURN_REQUESTED': // 반품 요청중
      return [ORDER_PRODUCT_ACTIONS.CANCEL_CLAIM];
    case 'PRODUCT_CANCEL_REQUESTED': // 취소 요청중 (판매자 승인 대기)
    case 'PRODUCT_RETURNING': // 반품 진행중 (상품 회수중)
    case 'PRODUCT_RETURNED': // 반품완료
    case 'PRODUCT_RETURN_REJECTED': // 반품 거절됨
      return [];
    default:
      return [];
  }
};
